import { type CustomDataSourcesFormData } from '../components/EditOrCreateModel/AccountStep/CustomDataSourcesFlyout'

import { CUSTOM_DATA_SOURCES_SUPPORTED_PLATFORMS, CustomDataSourceType } from 'shared/api/attributionModels'

import {
  useMutation,
  useQuery,
  useQueries,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult
} from '@tanstack/react-query'
import * as analytics from 'shared/api/analytics'
import * as integrationAttributions from 'shared/api/attributionModels'
import * as accounts from 'shared/api/accounts'
import type * as fetch from 'shared/api/fetch'
import useMutationAnalytics from 'shared/hooks/useMutationAnalytics'
import { isNilOrEmpty } from 'shared/utils'

export const queryKeys = {
  integrationAttribution: 'integratedAttributions',
  integratedAttributionConfigStatus: 'integratedAttributionConfigStatus',
  integratedAttributionDagStatus: 'integratedAttributionDagStatus',
  budgetOptimser: 'budgetOptimiser'
}

export const useQueryIntegratedAttributions = (
  clientId: number
): UseQueryResult<integrationAttributions.IntegrationAttributionsResp, fetch.ClientError> => {
  return useQuery({
    queryKey: [queryKeys.integrationAttribution, clientId],
    queryFn: async () => await integrationAttributions.getIntegrationAtributions(clientId),
    enabled: Boolean(clientId)
  })
}

export const useQueryIntegratedAttribution = (
  modelId: number,
  clientId = 0,
  enabled = true
): UseQueryResult<integrationAttributions.AttributionResp, fetch.ClientError> => {
  return useQuery({
    queryKey: [queryKeys.integrationAttribution, modelId, clientId],
    queryFn: async () => await integrationAttributions.getIntegrationAtribution(modelId, clientId),
    enabled: Boolean(clientId) && enabled && !Number.isNaN(modelId)
  })
}

interface ValidationDetail {
  errorCode: string
  field: string
  fieldValue: string
  message: string
}

export interface ValidationDetails {
  validationErrors: ValidationDetail[]
  dataSourceId: string
}

interface ValidationResp {
  message: string
  dataSourceId: string
  details: ValidationDetails[]
}

export type AttributionQueryResult = UseQueryResult<ValidationResp, fetch.ClientError>

interface DataSourceParams {
  dataSourceId: string
  urlParams: URLSearchParams
}

const getDataSourceIdAndUrlParams = (
  dataSource: CustomDataSourcesFormData,
  clientId: number,
  channelGroupingId: number
): DataSourceParams => {
  let dataSourceId = ''
  const urlParams = new URLSearchParams({
    account_id: dataSource.accountId?.toString(),
    client_id: clientId.toString(),
    platform: dataSource.platform,
    type: dataSource.type
  })

  if (dataSource.type !== CustomDataSourceType.TARGET && channelGroupingId !== undefined) {
    urlParams.append('channel_grouping_id', channelGroupingId.toString())
  }
  switch (dataSource.platform) {
    case CUSTOM_DATA_SOURCES_SUPPORTED_PLATFORMS.GOOGLE_SHEETS:
      dataSourceId = `${dataSource.accountId}`
      break
    case CUSTOM_DATA_SOURCES_SUPPORTED_PLATFORMS.GOOGLE_BIGQUERY:
      dataSourceId = `${dataSource.accountId}.${dataSource.datasetId ?? ''}.${dataSource.tableId ?? ''}`
      urlParams.append('dataset_id', dataSource.datasetId ?? '')
      urlParams.append('table_id', dataSource.tableId ?? '')
      break
    default:
      break
  }
  return { dataSourceId, urlParams }
}

export const useQueryValidateCustomDataSources = (
  clientId: number,
  channelGroupingId: number,
  enabled: boolean,
  customDataSources: CustomDataSourcesFormData[] = []
): AttributionQueryResult[] => {
  const validationQueries = useQueries({
    queries: customDataSources.map((dataSource) => {
      const { dataSourceId, urlParams } = getDataSourceIdAndUrlParams(dataSource, clientId, channelGroupingId)
      return {
        queryKey: [queryKeys.integrationAttribution, clientId, channelGroupingId, dataSource],
        queryFn: async () => {
          try {
            const response = await integrationAttributions.validateCustomDataSources(urlParams)
            return { ...response, dataSourceId }
          } catch (error) {
            const clientError = error as fetch.ApiClientError
            clientError.details = { ...clientError.details, dataSourceId }
            throw clientError
          }
        },
        enabled,
        throwOnError: false
      }
    })
  })
  return validationQueries as unknown as AttributionQueryResult[]
}

export const useQueryDagStatusByModelId = (
  clientId: number,
  modelId: number,
  enabled = true,
  useCache = true
): UseQueryResult<[analytics.DagStatusResp] | [], fetch.ClientError> => {
  return useQuery({
    queryKey: [queryKeys.integratedAttributionDagStatus, clientId, modelId, useCache],

    queryFn: async () =>
      await analytics.postConfigStatusById<[analytics.DagStatusResp] | []>(
        'attribution_dag',
        clientId,
        modelId,
        useCache
      ),

    enabled: Boolean(clientId) && enabled
  })
}

export const useMutationAnalyticsAttributionChannelAggregated = (): UseMutationResult<
  analytics.AttributionChannelAggregatedResponseV2[],
  fetch.ClientError,
  analytics.AnalyticsReq
> => {
  return useMutation({
    mutationFn: async (data) => await analytics.postAttributionChannelAggregatedV2(data)
  })
}

export const useMutationAnalyticsAttribution = (): UseMutationResult<
  analytics.AttributionDateChannelAggregatedResponseV2[],
  fetch.ClientError,
  analytics.AnalyticsReq
> => {
  return useMutation({
    mutationFn: async (data) => await analytics.postAttributionDateChannelAggregatedV2(data)
  })
}

export const useMutationAnalyticsAttributionDates = (): UseMutationResult<
  analytics.DatesInAttributionReportResp[],
  fetch.ClientError,
  analytics.AnalyticsReq
> => {
  return useMutationAnalytics(async (data) => await analytics.postGetDatesInAttributionReport(data))
}

export const useQueryAttributionConfigStatuses = (
  clientId: number,
  enabled = true
): UseQueryResult<analytics.ConfigStatusResp[], fetch.ClientError> => {
  return useQuery({
    queryKey: [queryKeys.integratedAttributionConfigStatus, clientId],

    queryFn: async () => {
      return await analytics.postConfigStatuses('attribution_client_configs_status_ui', clientId, true)
    },

    enabled
  })
}

interface AttributionConfigStatusByIdReq {
  clientId: number
  modelId: number
}
export const useMutationAttributionConfigStatusById = (): UseMutationResult<
  [analytics.ConfigStatusResp],
  fetch.ClientError,
  AttributionConfigStatusByIdReq
> => {
  return useMutationAnalytics(async (data: AttributionConfigStatusByIdReq) => {
    return await analytics.postConfigStatusById<[analytics.ConfigStatusResp]>(
      'attribution_model_config_status_ui',
      data.clientId,
      data.modelId,
      true
    )
  })
}

export const useMutationDeleteConfig = (): UseMutationResult<
  integrationAttributions.AttributionResp,
  fetch.ClientError,
  integrationAttributions.ConfigIdAndClientIdReq
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: integrationAttributions.deleteIntegrationAtribution,
    onMutate: async (deletedConfig) => {
      await queryClient.cancelQueries({ queryKey: [queryKeys.budgetOptimser, deletedConfig.clientId] })
      await queryClient.cancelQueries({ queryKey: [queryKeys.integrationAttribution, deletedConfig.clientId] })

      const existingBudgetOptimiserConfigs = queryClient.getQueryData([
        queryKeys.budgetOptimser,
        deletedConfig.clientId
      ])
      const existingAttributionConfigs = queryClient.getQueryData<integrationAttributions.IntegrationAttributionsResp>([
        queryKeys.integrationAttribution,
        deletedConfig.clientId
      ])

      queryClient.setQueryData<integrationAttributions.AttributionResp[]>(
        [queryKeys.budgetOptimser, deletedConfig.clientId],
        (oldConfigs) => {
          return oldConfigs == null ? [] : oldConfigs.filter((config) => config.id !== deletedConfig.modelId)
        }
      )

      queryClient.setQueryData<integrationAttributions.IntegrationAttributionsResp>(
        [queryKeys.integrationAttribution, deletedConfig.clientId],
        (oldConfigs) => {
          return oldConfigs == null ? [] : oldConfigs.filter((config) => config.id !== deletedConfig.modelId)
        }
      )

      return { existingBudgetOptimiserConfigs, existingAttributionConfigs }
    },
    onError: (_, variables, context) => {
      queryClient.setQueryData([queryKeys.budgetOptimser, variables.clientId], context?.existingBudgetOptimiserConfigs)
      queryClient.setQueryData(
        [queryKeys.integrationAttribution, variables.clientId],
        context?.existingAttributionConfigs
      )
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKeys.budgetOptimser] })
      void queryClient.invalidateQueries({ queryKey: [queryKeys.integrationAttribution] })
    }
  })
}

export const useMutationCreateAttribution = (): UseMutationResult<
  integrationAttributions.AttributionResp,
  fetch.ClientError,
  integrationAttributions.AttributionModelReq
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: integrationAttributions.createIntegrationAtribution,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKeys.integrationAttribution] })
      void queryClient.invalidateQueries({ queryKey: [queryKeys.budgetOptimser] })
    }
  })
}

export const useMutationUpdateAttribution = (): UseMutationResult<
  integrationAttributions.AttributionResp,
  fetch.ClientError,
  { data: integrationAttributions.AttributionModelReq; configId: number }
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: integrationAttributions.updateIntegrationAtribution,
    onMutate: async (updatedConfigData) => {
      await queryClient.cancelQueries({
        queryKey: [queryKeys.integrationAttribution]
      })
      await queryClient.cancelQueries({
        queryKey: [queryKeys.budgetOptimser]
      })

      const clientIdStr = updatedConfigData.data.clientId.toString()

      const previousConfig = queryClient.getQueryData<integrationAttributions.AttributionResp>([
        queryKeys.integrationAttribution,
        updatedConfigData.configId,
        clientIdStr
      ])

      const previousList = queryClient.getQueryData<integrationAttributions.IntegrationAttributionsResp>([
        queryKeys.integrationAttribution,
        clientIdStr
      ])

      if (previousConfig != null) {
        queryClient.setQueryData<integrationAttributions.AttributionResp>(
          [queryKeys.integrationAttribution, updatedConfigData.configId, clientIdStr],
          { ...previousConfig, name: updatedConfigData.data.name, runSchedule: updatedConfigData.data.runSchedule }
        )
      }

      if (previousList != null) {
        queryClient.setQueryData<integrationAttributions.IntegrationAttributionsResp>(
          [queryKeys.integrationAttribution, clientIdStr],
          previousList.map((config) =>
            config.id === updatedConfigData.configId
              ? { ...config, name: updatedConfigData.data.name, runSchedule: updatedConfigData.data.runSchedule }
              : config
          ) as integrationAttributions.IntegrationAttributionsResp
        )
      }

      return { previousConfig, previousList, updatedConfigData }
    },
    onError: (_, __, context) => {
      const clientIdStr = context?.updatedConfigData.data.clientId.toString()
      context?.previousConfig != null &&
        queryClient.setQueryData(
          [queryKeys.integrationAttribution, context?.updatedConfigData.configId, clientIdStr],
          context.previousConfig
        )
      queryClient.setQueryData(
        [queryKeys.budgetOptimser, context?.updatedConfigData.configId, clientIdStr],
        context?.previousConfig
      )

      if (context?.previousList != null) {
        queryClient.setQueryData([queryKeys.integrationAttribution, clientIdStr], context.previousList)
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKeys.integrationAttribution] })
      void queryClient.invalidateQueries({ queryKey: [queryKeys.budgetOptimser] })
    }
  })
}

type BulkDeleteConfigResult = Array<{
  modelId: number
  success: boolean
  error?: fetch.ClientError
}>

export const useBulkMutationDeleteConfigs = ({
  clientId
}: {
  clientId: number
}): UseMutationResult<BulkDeleteConfigResult, fetch.ClientError, number[]> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (modelIds: number[]): Promise<BulkDeleteConfigResult> => {
      const deletePromises = modelIds.map(async (modelId) => {
        try {
          await integrationAttributions.deleteIntegrationAtribution({
            clientId,
            modelId
          })
          return { modelId, success: true }
        } catch (error) {
          return { modelId, success: false, error: error as fetch.ClientError }
        }
      })

      return await Promise.all(deletePromises)
    },

    onSuccess: async (results, modelIds): Promise<void> => {
      for (const result of results) {
        if (result.success) {
          queryClient.removeQueries({
            queryKey: [queryKeys.integrationAttribution, result.modelId, clientId]
          })
          queryClient.removeQueries({
            queryKey: [queryKeys.budgetOptimser, result.modelId, clientId]
          })
        }
      }
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.integrationAttribution, clientId]
      })
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.budgetOptimser, clientId]
      })
    }
  })
}

interface BulkUpdateAttributionRequest {
  data: integrationAttributions.AttributionModelReq
  configId: number
}

type BulkUpdateAttributionResult = Array<{
  configId: number
  success: boolean
  data?: integrationAttributions.AttributionResp
  error?: fetch.ClientError
}>

export const useBulkMutationUpdateAttribution = ({
  clientId
}: {
  clientId: number
}): UseMutationResult<BulkUpdateAttributionResult, fetch.ClientError, BulkUpdateAttributionRequest[]> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (updates: BulkUpdateAttributionRequest[]): Promise<BulkUpdateAttributionResult> => {
      const updatePromises = updates.map(async (updateRequest) => {
        try {
          const result = await integrationAttributions.updateIntegrationAtribution({
            data: updateRequest.data,
            configId: updateRequest.configId
          })
          return { configId: updateRequest.configId, success: true, data: result }
        } catch (error) {
          return { configId: updateRequest.configId, success: false, error: error as fetch.ClientError }
        }
      })

      return await Promise.all(updatePromises)
    },

    onSuccess: async (results): Promise<void> => {
      for (const result of results) {
        if (result.success && result.data != null) {
          queryClient.setQueryData([queryKeys.integrationAttribution, result.configId, clientId], result.data)
          queryClient.setQueryData([queryKeys.budgetOptimser, result.configId, clientId], result.data)
        }
      }
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.integrationAttribution]
      })
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.budgetOptimser]
      })
    }
  })
}

export const useQueryGcpDatasetMetadata = (
  clientId: number,
  accountId?: number,
  datasetId?: string
): UseQueryResult<accounts.GCPDatasetMetadataResp, fetch.ClientError> => {
  return useQuery({
    queryKey: [queryKeys.integrationAttribution, clientId, accountId, datasetId],
    queryFn: async () => {
      if (isNilOrEmpty(datasetId)) {
        return { bigqueryTables: [], datasetDetails: {} } as unknown as accounts.GCPDatasetMetadataResp
      }
      return await accounts.getGCPDatasetMetadata(clientId, accountId ?? 0, datasetId)
    },
    enabled: datasetId != null && accountId != null
  })
}
